/**
 * tasks.js
 *
 * Client-side helper for submitting a whitelist task entry.
 * Insert-only by design — see supabase/tasks.sql for the table and RLS
 * policies that enforce this at the database level too.
 *
 * The checks here are UX niceties (fast, friendly feedback). The real
 * boundary is the Postgres CHECK constraints and the RLS policy, which
 * validate independently of anything this file does.
 */
import { supabase } from './supabaseClient';

const EVM_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const TWEET_URL_RE = /^https?:\/\/(www\.)?(x|twitter)\.com\/.+/i;

/**
 * @param {object} entry
 * @param {string} entry.walletAddress   ARC/EVM address
 * @param {string} entry.quoteTweetUrl   Link to the user's quote tweet
 * @param {string} [entry.xHandle]       Their X handle, for verification
 * @param {object} entry.tasksCompleted  { follow: true, like: true, ... }
 * @returns {Promise<{ alreadyExists: boolean }>}
 * @throws {Error} on validation failure or an insert error
 */
export async function submitTasks({ walletAddress, quoteTweetUrl, xHandle, tasksCompleted }) {
  const wallet = String(walletAddress || '').trim().toLowerCase();
  const url = String(quoteTweetUrl || '').trim();
  const handle = String(xHandle || '').trim().replace(/^@/, '') || null;

  if (!EVM_ADDRESS_RE.test(wallet)) {
    throw new Error('Invalid wallet — must be 0x followed by 40 hex characters.');
  }
  if (!TWEET_URL_RE.test(url)) {
    throw new Error('Quote tweet link must be an x.com or twitter.com URL.');
  }

  const { error } = await supabase.from('hexabots_tasks').insert({
    wallet_address: wallet,
    quote_tweet_url: url,
    x_handle: handle,
    tasks_completed: tasksCompleted || {},
    // status intentionally omitted — the DB defaults it to 'pending'
    // and the RLS policy rejects any attempt to set it otherwise.
  });

  if (error) {
    if (error.code === '23505') return { alreadyExists: true }; // unique_violation
    if (error.code === '23514') throw new Error('Submission rejected — check your wallet address and tweet link.');
    throw error;
  }

  return { alreadyExists: false };
}
